import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosClient from "../api/axiosClient";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import "./admin.css";
import "./ManageBikes.css";

const getBikeImageUrl = (image) => {
  if (!image) return "/placeholder-bike.jpg";

  const cleanImage = image.trim();

  if (
    cleanImage.startsWith("http://") ||
    cleanImage.startsWith("https://")
  ) {
    return cleanImage;
  }

  return `https://bros-bike-rental-api.onrender.com/${cleanImage.replace(/^\/+/, "")}`;
};

function ManageBikes() {
  const [bikes, setBikes] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchBikes = useCallback(() => {
    setLoading(true);
    axiosClient
      .get("/admin/bikes")
      .then((res) => setBikes(res.data.bikes || res.data))
      .catch(() => toast.error("Failed to load bikes"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchBikes();
  }, [fetchBikes]);

  const deleteBike = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "var(--danger)",
      cancelButtonColor: "var(--surface3)",
      confirmButtonText: "Yes, delete it!",
      background: "var(--surface)",
      color: "var(--text)",
    });

    if (!result.isConfirmed) return;

    try {
      await axiosClient.delete(`/admin/bikes/${id}`);
      fetchBikes();
      toast.success("Bike deleted successfully!");
    } catch {
      toast.error("Failed to delete bike");
    }
  };

  const toggleAvailability = async (id) => {
    try {
      await axiosClient.put(`/admin/bikes/toggle/${id}`);
      fetchBikes();
      toast.success("Status updated!");
    } catch {
      toast.error("Failed to update bike status");
    }
  };

  // const toggleBlock = async (bike) => {
  //   if (bike.isBlocked) {
  //     // Unblock immediately
  //     const result = await Swal.fire({
  //       title: "Unblock Bike?",
  //       text: "This bike will become available on the website again.",
  //       icon: "question",
  //       showCancelButton: true,
  //       confirmButtonColor: "#22c55e",
  //       cancelButtonColor: "#6b7280",
  //       confirmButtonText: "Yes, Unblock",
  //       background: "var(--surface)",
  //       color: "var(--text)",
  //     });
  //     if (!result.isConfirmed) return;
  //     try {
  //       await axiosClient.put(`/admin/bikes/unblock/${bike._id}`);
  //       fetchBikes();
  //       toast.success("Bike unblocked!");
  //     } catch {
  //       toast.error("Failed to unblock bike");
  //     }
  //   } else {
  //     // Show time-range picker
  //     const now = new Date();
  //     const pad = (n) => String(n).padStart(2, "0");
  //     const localDateTimeStr = (d) =>
  //       `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;

  //     const defaultFrom = localDateTimeStr(now);
  //     const defaultUntil = localDateTimeStr(new Date(now.getTime() + 24 * 60 * 60 * 1000));

  //     const { value: formValues } = await Swal.fire({
  //       title: "🔒 Block Bike (Offline Booking)",
  //       html: `
  //         <p style="margin-bottom:12px; color:#555; font-size:13px;">
  //           Set the time range this bike is blocked. It will auto-unblock when the period ends.
  //         </p>
  //         <label style="display:block; text-align:left; font-size:12px; font-weight:600; color:#374151; margin-bottom:4px;">Block From</label>
  //         <input id="swal-from" type="datetime-local" class="swal2-input" value="${defaultFrom}" style="width:90%; margin-bottom:12px;">
  //         <label style="display:block; text-align:left; font-size:12px; font-weight:600; color:#374151; margin-bottom:4px;">Block Until</label>
  //         <input id="swal-until" type="datetime-local" class="swal2-input" value="${defaultUntil}" style="width:90%;">
  //       `,
  //       confirmButtonText: "Block Bike",
  //       confirmButtonColor: "#f97316",
  //       cancelButtonColor: "#6b7280",
  //       showCancelButton: true,
  //       background: "var(--surface)",
  //       color: "var(--text)",
  //       preConfirm: () => {
  //         const from = document.getElementById("swal-from").value;
  //         const until = document.getElementById("swal-until").value;
  //         if (!from || !until) {
  //           Swal.showValidationMessage("Please fill in both dates");
  //           return false;
  //         }
  //         if (new Date(until) <= new Date(from)) {
  //           Swal.showValidationMessage("'Block Until' must be after 'Block From'");
  //           return false;
  //         }
  //         return { blockedFrom: from, blockedUntil: until };
  //       },
  //     });

  //     if (!formValues) return;

  //     try {
  //       await axiosClient.put(`/admin/bikes/block/${bike._id}`, {
  //         blockedFrom: formValues.blockedFrom,
  //         blockedUntil: formValues.blockedUntil,
  //       });
  //       fetchBikes();
  //       toast.success("Bike blocked for offline booking!");
  //     } catch {
  //       toast.error("Failed to block bike");
  //     }
  //   }
  // };

  return (
    <>
      <div className="admin-page-header">
        <h1>Manage Bikes</h1>
        <p>{bikes.length} bike{bikes.length !== 1 ? "s" : ""} listed in the fleet</p>
      </div>

      {loading ? (
        /* ── Skeleton rows ── */
        <div className="admin-skel-wrap">
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              className="admin-skel-row"
              style={{ animationDelay: `${i * 0.07}s` }}
            />
          ))}
        </div>
      ) : bikes.length === 0 ? (
        <div className="admin-empty">
          <div className="empty-icon"></div>
          <p>No bikes found. Add one to get started.</p>
        </div>
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Image</th>
                <th>Name</th>
                <th>Type</th>
                <th>Price / Day</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {bikes.map((bike) => (
                <tr key={bike._id}>
                  <td>
                    <img
                      src={getBikeImageUrl(bike.image)}
                      alt={bike.name}
                      width="120"
                      height="80"
                      style={{ objectFit: "cover", borderRadius: "20px" }}
                    />
                  </td>
                  <td style={{ fontWeight: 600 }}>{bike.name}</td>
                  <td style={{ color: "var(--muted)" }}>{bike.type}</td>
                  <td>₹ {bike.pricePerDay}</td>
                  <td>
                    <span
                      className={`badge ${bike.isBlocked
                        ? "badge-orange"
                        : bike.isAvailable
                          ? "badge-green"
                          : "badge-red"
                        }`}
                    >
                      {bike.isBlocked ? "🔒 Blocked (Offline)" : bike.isAvailable ? "Available" : "Disabled"}
                    </span>
                    {bike.isBlocked && bike.blockedFrom && bike.blockedUntil && (
                      <div style={{ fontSize: "10px", color: "#f97316", marginTop: "4px", lineHeight: 1.4 }}>
                        <div>From: {new Date(bike.blockedFrom).toLocaleString()}</div>
                        <div>Until: {new Date(bike.blockedUntil).toLocaleString()}</div>
                      </div>
                    )}
                  </td>
                  <td>
                    <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => navigate(`/admin/edit-bike/${bike._id}`)}
                      >
                        Edit
                      </button>
                      <button
                        className={`btn btn-sm ${bike.isAvailable ? "btn-warning" : "btn-success"}`}
                        onClick={() => toggleAvailability(bike._id)}
                      >
                        {bike.isAvailable ? "Disable" : "Enable"}
                      </button>
                      {/* <button
                        className={`btn btn-sm ${bike.isBlocked ? "btn-success" : "btn-block"}`}
                        onClick={() => toggleBlock(bike)}
                      >
                        {bike.isBlocked ? "Unblock" : "Block"}
                      </button> */}
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => deleteBike(bike._id)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

export default ManageBikes;
